import React, { useState, useMemo, useCallback, useRef } from "react";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";
import SerialInputPopup from "./SerialInputPopup";

/** Standalone serial-like query (e.g. 6 digits or short alphanumeric) with no Hebrew */
function looksLikeStandaloneSerialQuery(raw) {
  const t = String(raw || "").trim();
  if (!t || /[\u0590-\u05FF]/.test(t)) return false;
  if (/^\d{6}$/.test(t)) return true;
  if (/^\d{5,8}$/.test(t)) return true;
  if (/^[A-Za-z0-9/-]{4,24}$/.test(t)) return true;
  return false;
}

/**
 * Inline item search — replaces AddItemModal.
 * Shows a single input; dropdown merges serial matches + name matches in real time.
 */
const ItemSearchInline = ({
  inventory,
  items,
  cart,
  getRemainingQuantity,
  onAddItem,
  isChildUser = false,
  childUserPersonalNumber = null,
  /** Navigate to Inventory with optional { serialNumber, itemDefinitionId } prefill (admin/parent only). */
  onAddStockShortcut = null,
}) => {
  const [term, setTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [serialPopup, setSerialPopup] = useState(null); // { itemName, itemDefinitionId, availableSerials }
  const [warning, setWarning] = useState("");
  const inputRef = useRef(null);

  // Build flat searchable list
  const searchList = useMemo(() => {
    if (!inventory?.length || !items?.length) return [];

    const results = [];
    const namesSeen = new Set();

    inventory.forEach((invItem) => {
      const itemDef = items.find((i) => i.id === invItem.itemDefinitionId);
      if (!itemDef) return;

      if (itemDef.isChecked) {
        // Serial item — add serial row
        if (!invItem.serialNumber) return;

        let available = false;
        if (isChildUser && childUserPersonalNumber) {
          const first = invItem.assignments?.first?.[childUserPersonalNumber];
          const second = invItem.assignments?.second?.[childUserPersonalNumber];
          available = !!first && !second;
        } else {
          available = invItem.status === "available";
        }
        if (!available) return;

        // Check not already in cart
        if (cart.find((c) => c.serial === invItem.serialNumber)) return;

        results.push({
          type: "serial",
          key: `serial-${invItem.id}`,
          label: `${itemDef.itemName} — ${invItem.serialNumber}`,
          itemName: itemDef.itemName,
          itemDefinitionId: itemDef.id,
          serial: invItem.serialNumber,
          invId: invItem.id,
        });

        // Also add name row (once per item name)
        if (!namesSeen.has(itemDef.itemName)) {
          namesSeen.add(itemDef.itemName);
          // Count available serials
          const availableSerials = inventory.filter((inv) => {
            if (inv.itemDefinitionId !== itemDef.id || !inv.serialNumber) return false;
            if (isChildUser && childUserPersonalNumber) {
              const first = inv.assignments?.first?.[childUserPersonalNumber];
              const second = inv.assignments?.second?.[childUserPersonalNumber];
              return !!first && !second;
            }
            return inv.status === "available";
          });
          results.push({
            type: "name-serial",
            key: `name-serial-${itemDef.id}`,
            label: `${itemDef.itemName} (${availableSerials.length} זמינים)`,
            itemName: itemDef.itemName,
            itemDefinitionId: itemDef.id,
            availableCount: availableSerials.length,
          });
        }
      } else {
        // Non-serial item — add name row once
        const suffix = invItem.isOrganic ? "אורגני" : "קווי";
        const nameKey = `${itemDef.itemName}-${suffix}`;
        if (namesSeen.has(nameKey)) return;
        namesSeen.add(nameKey);

        const available = getRemainingQuantity(itemDef.id, invItem.isOrganic) || 0;

        results.push({
          type: "name-nonserial",
          key: `name-ns-${itemDef.id}-${suffix}`,
          label: `${itemDef.itemName} (${suffix}) — זמין: ${available}`,
          itemName: itemDef.itemName,
          itemDefinitionId: itemDef.id,
          isOrganic: invItem.isOrganic,
          available,
          invId: invItem.id,
        });
      }
    });

    return results;
  }, [inventory, items, cart, isChildUser, childUserPersonalNumber, getRemainingQuantity]);

  // Filter by search term
  const filtered = useMemo(() => {
    if (!term.trim()) return [];
    const t = normalizeItemSearchText(term);
    return searchList.filter((row) =>
      normalizeItemSearchText(row.label).includes(t)
    );
  }, [searchList, term]);

  const showMissingStandaloneSerialHint =
    Boolean(term.trim()) &&
    filtered.length === 0 &&
    looksLikeStandaloneSerialQuery(term);

  const getAvailableSerials = useCallback(
    (itemDefinitionId) => {
      return inventory
        .filter((inv) => {
          if (inv.itemDefinitionId !== itemDefinitionId || !inv.serialNumber) return false;
          if (cart.find((c) => c.serial === inv.serialNumber)) return false;
          if (isChildUser && childUserPersonalNumber) {
            const first = inv.assignments?.first?.[childUserPersonalNumber];
            const second = inv.assignments?.second?.[childUserPersonalNumber];
            return !!first && !second;
          }
          return inv.status === "available";
        })
        .map((inv) => inv.serialNumber);
    },
    [inventory, cart, isChildUser, childUserPersonalNumber]
  );

  const buildCartItem = useCallback(
    (row, serial = null) => {
      if (row.type === "serial" || serial) {
        const resolvedSerial = serial || row.serial;
        const invItem = inventory.find(
          (inv) =>
            inv.itemDefinitionId === row.itemDefinitionId &&
            inv.serialNumber === resolvedSerial
        );
        return {
          id: invItem?.id || Date.now().toString(),
          item: row.itemName,
          serial: resolvedSerial,
          quantity: 1,
          note: "",
          itemDefinitionId: row.itemDefinitionId,
          hasSerialNumber: true,
          isOrganic: null,
        };
      }
      // non-serial
      const invItem = inventory.find(
        (inv) =>
          inv.itemDefinitionId === row.itemDefinitionId &&
          (row.isOrganic === null || inv.isOrganic === row.isOrganic) &&
          inv.status === "available"
      );
      return {
        id: invItem?.id || Date.now().toString(),
        item: row.label.split(" (")[0], // base name
        serial: "",
        quantity: 1,
        note: "",
        itemDefinitionId: row.itemDefinitionId,
        hasSerialNumber: false,
        isOrganic: row.isOrganic,
        availableQuantity: row.available,
      };
    },
    [inventory]
  );

  const handleSelect = useCallback(
    (row) => {
      setTerm("");
      setShowDropdown(false);
      setWarning("");

      if (row.type === "serial") {
        // Add directly
        onAddItem(buildCartItem(row));
      } else if (row.type === "name-serial") {
        if (row.availableCount === 0) {
          setWarning(`אין פריטים זמינים מסוג "${row.itemName}"`);
          return;
        }
        // Open serial popup
        setSerialPopup({
          itemName: row.itemName,
          itemDefinitionId: row.itemDefinitionId,
          availableSerials: getAvailableSerials(row.itemDefinitionId),
          row,
        });
      } else {
        // non-serial
        if (row.available <= 0) {
          setWarning(`אין פריטים זמינים מסוג "${row.itemName}" (${row.isOrganic ? "אורגני" : "קווי"})`);
          return;
        }
        onAddItem(buildCartItem(row));
      }
    },
    [onAddItem, buildCartItem, getAvailableSerials]
  );

  const handleSerialConfirm = useCallback(
    (serial) => {
      if (!serialPopup) return;
      // Check not already in cart
      if (cart.find((c) => c.serial === serial)) {
        setWarning(`מספר סידורי ${serial} כבר נמצא בעגלה`);
        setSerialPopup(null);
        return;
      }
      onAddItem(buildCartItem(serialPopup.row, serial));
      setSerialPopup(null);
    },
    [serialPopup, cart, onAddItem, buildCartItem]
  );

  return (
    <div className="item-search-inline">
      <div className="item-search-label">הוסף פריט לעגלה:</div>
      <div className="item-search-input-wrap">
        <input
          ref={inputRef}
          className="item-search-input"
          placeholder="חפש פריט לפי שם או מספר סידורי..."
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setShowDropdown(true);
            setWarning("");
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          autoComplete="off"
        />
        {showDropdown && filtered.length > 0 && (
          <div className="item-search-dropdown">
            {filtered.map((row) => (
              <div
                key={row.key}
                className={`item-search-option ${row.type}`}
                onMouseDown={() => handleSelect(row)}
              >
                {row.label}
              </div>
            ))}
          </div>
        )}
      </div>

        {warning && <div className="item-search-warning">{warning}</div>}

      {showMissingStandaloneSerialHint && (
        <div className="signature-item-search-missing-serial">
          <p>לא נמצאה התאמה לחיפוש במלאי הזמין.</p>
          <div className="signature-item-search-missing-serial-actions">
            {typeof onAddStockShortcut === "function" && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onAddStockShortcut({
                    serialNumber: term.trim(),
                    itemDefinitionId: "",
                  });
                  setTerm("");
                  setShowDropdown(false);
                }}
              >
                הוסף למלאי (מקיצור דרך)
              </button>
            )}
            {typeof onAddStockShortcut !== "function" && (
              <p className="signature-missing-serial-child-hint">
                פנה למנהל להוספת המספר במלאי, ואז חפש שוב כאן.
              </p>
            )}
          </div>
        </div>
      )}

      {serialPopup && (
        <SerialInputPopup
          itemName={serialPopup.itemName}
          itemDefinitionId={serialPopup.itemDefinitionId}
          availableSerials={serialPopup.availableSerials}
          onConfirm={handleSerialConfirm}
          onCancel={() => setSerialPopup(null)}
          onAddStockShortcut={onAddStockShortcut}
        />
      )}
    </div>
  );
};

export default ItemSearchInline;
